import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import Breadcrumbs from '../components/shared/Breadcrumbs';
import AffiliateBanner from '../components/shared/AffiliateBanner';
import CTABox from '../components/shared/CTABox';
import TableOfContents from '../components/shared/TableOfContents';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const CanadaPage = () => {
  const tocItems = [
    { id: 'unternehmensformen', title: 'Unternehmensformen in Kanada', level: 2 },
    { id: 'vorteile', title: 'Vorteile einer kanadischen Corporation', level: 2 },
    { id: 'federal-vs-provincial', title: 'Federal vs. Provincial', level: 2 },
    { id: 'schritt-fuer-schritt', title: 'Schritt-für-Schritt Anleitung', level: 2 },
    { id: 'kosten', title: 'Kosten und Gebühren', level: 2 },
    { id: 'steuern', title: 'Steuern in Kanada', level: 2 },
    { id: 'faq', title: 'Häufig gestellte Fragen', level: 2 },
  ];

  const provincialFees = [
    { province: 'Federal (CBCA)', fee: '$200 CAD (online)', processing: '1-2 Tage' },
    { province: 'Ontario', fee: '$360 CAD', processing: '1-3 Tage' },
    { province: 'British Columbia', fee: '$350 CAD', processing: '1-2 Tage' },
    { province: 'Alberta', fee: '$275 CAD', processing: '1-2 Tage' },
    { province: 'Quebec', fee: '$360 CAD', processing: '2-5 Tage' },
  ];

  const faqs = [
    {
      question: 'Kann ich als Ausländer eine kanadische Corporation gründen?',
      answer: 'Ja, Nicht-Kanadier können eine kanadische Corporation gründen. Bei einer Federal Corporation (CBCA) muss mindestens 25% des Board of Directors aus Canadian Residents bestehen. Bei einigen Provinzen (z.B. British Columbia) gibt es keine Residenzanforderungen für Directors.'
    },
    {
      question: 'Was ist der Unterschied zwischen Federal und Provincial Incorporation?',
      answer: 'Eine Federal Corporation wird unter dem Canada Business Corporations Act gegründet und hat das Recht, in ganz Kanada Geschäfte zu machen. Eine Provincial Corporation wird unter dem Recht einer bestimmten Provinz gegründet und benötigt zusätzliche Registrierungen, um in anderen Provinzen tätig zu sein.'
    },
    {
      question: 'Brauche ich ein Büro in Kanada?',
      answer: 'Ja, sowohl Federal als auch Provincial Corporations benötigen eine Registered Office Address in Kanada. Für internationale Gründer bieten Virtual Office Services diese Adresse an.'
    },
    {
      question: 'Wie hoch ist die Körperschaftsteuer in Kanada?',
      answer: 'Der kombinierte Bundessteuersatz liegt bei etwa 26-31% (je nach Provinz). Für kleine Unternehmen mit aktivem Geschäftseinkommen bis $500.000 CAD gibt es einen reduzierten Satz von etwa 9-12% (Small Business Deduction).'
    },
    {
      question: 'Was ist eine NUANS-Suche?',
      answer: 'NUANS (Newly Upgraded Automated Name Search) ist eine obligatorische Namensdatenbank-Suche für Federal Corporations und in einigen Provinzen. Sie stellt sicher, dass Ihr gewünschter Firmenname einzigartig ist und kostet ca. $20 CAD.'
    },
    {
      question: 'Kann ich ein kanadisches Bankkonto eröffnen?',
      answer: 'Ja, aber als Nicht-Resident ist es komplizierter. Einige Banken erlauben die Kontoeröffnung nur bei persönlicher Anwesenheit. Digitale Optionen wie Wise Business können eine Alternative sein.'
    },
    {
      question: 'Was ist eine Business Number (BN)?',
      answer: 'Die Business Number ist Ihre neunstellige Identifikationsnummer bei der Canada Revenue Agency (CRA). Sie benötigen sie für Steuerzwecke, Import/Export und zur Registrierung für GST/HST.'
    },
    {
      question: 'Gibt es ein Doppelbesteuerungsabkommen mit Deutschland?',
      answer: 'Ja, Deutschland und Kanada haben ein Doppelbesteuerungsabkommen, das die doppelte Besteuerung von Einkommen vermeidet. Die genauen Auswirkungen hängen von Ihrer individuellen Situation ab — konsultieren Sie einen Steuerberater.'
    },
  ];

  return (
    <div className="py-12" data-testid="canada-page">
      <div className="container-wide">
        <Breadcrumbs items={[{ label: 'Firma Gründen Kanada', href: '/firma-gruenden-kanada' }]} />
        
        <AffiliateBanner />

        <div className="grid lg:grid-cols-12 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-8">
            <article className="prose-reviewlex">
              {/* Hero */}
              <header className="mb-12">
                <h1 className="mb-6">Firma in Kanada gründen — Der komplette Leitfaden 2026</h1>
                <p className="text-xl text-slate-600 mb-6">
                  Alles über die Gründung einer kanadischen Corporation: Federal vs. Provincial, 
                  Kosten, Steuern und der Schritt-für-Schritt Gründungsprozess.
                </p>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span>Aktualisiert: April 2026</span>
                  <span>•</span>
                  <span>Lesezeit: 11 Min.</span>
                </div>
              </header>

              {/* Unternehmensformen */}
              <section id="unternehmensformen" className="mb-16">
                <h2>Unternehmensformen in Kanada</h2>
                <p>
                  Kanada bietet verschiedene Unternehmensformen für Gründer:
                </p>

                <div className="space-y-4 my-8">
                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">Corporation (Inc.)</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Die kanadische Kapitalgesellschaft mit beschränkter Haftung. Ähnlich einer GmbH oder AG. 
                      Die häufigste Wahl für internationale Gründer aufgrund des Haftungsschutzes.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">Sole Proprietorship</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Einzelunternehmen ohne separate rechtliche Identität. Einfach zu gründen, aber keine 
                      Haftungsbeschränkung. Für Nicht-Residenten schwierig aufgrund der Residenzanforderungen.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">Partnership</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Personengesellschaft mit zwei oder mehr Partnern. Limited Partnership (LP) und 
                      Limited Liability Partnership (LLP) bieten begrenzten Haftungsschutz.
                    </p>
                  </div>
                </div>

                <div className="card-highlight p-6">
                  <p className="mb-0 text-sm">
                    <strong>Für internationale Gründer empfehlen wir:</strong> Die Corporation (Inc.) ist 
                    für die meisten Situationen die beste Wahl — Haftungsschutz, professionelles Image 
                    und klare rechtliche Struktur.
                  </p>
                </div>
              </section>

              {/* Vorteile */}
              <section id="vorteile" className="mb-16">
                <h2>Vorteile einer kanadischen Corporation</h2>
                
                <div className="grid gap-6 mb-8">
                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      G7-Wirtschaft mit stabilem Rechtssystem
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Kanada ist Mitglied der G7 und verfügt über ein stabiles, verlässliches Rechtssystem. 
                      Das schafft Vertrauen bei Geschäftspartnern weltweit.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      Bilaterale Steuerabkommen
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Kanada hat Doppelbesteuerungsabkommen mit Deutschland und vielen anderen Ländern, 
                      was die internationale Steuerplanung erleichtert.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      Small Business Deduction
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Kleine Unternehmen profitieren von einem reduzierten Steuersatz von etwa 9-12% 
                      auf die ersten $500.000 CAD aktivem Geschäftseinkommen.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      Zugang zu US- und europäischen Märkten
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      USMCA-Abkommen mit USA und Mexiko, CETA-Abkommen mit der EU — Kanada ist ein 
                      strategischer Standort für internationalen Handel.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      Innovationsfreundliches Umfeld
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      SR&ED (Scientific Research and Experimental Development) Tax Credits bieten 
                      attraktive Steuervorteile für R&D-intensive Unternehmen.
                    </p>
                  </div>
                </div>
              </section>

              {/* Federal vs Provincial */}
              <section id="federal-vs-provincial" className="mb-16">
                <h2>Federal vs. Provincial Incorporation</h2>
                
                <div className="overflow-x-auto mb-8">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead>Aspekt</TableHead>
                        <TableHead>Federal (CBCA)</TableHead>
                        <TableHead>Provincial</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">Namensschutz</TableCell>
                        <TableCell>Kanadaweit</TableCell>
                        <TableCell>Nur in der Provinz</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Geschäftstätigkeit</TableCell>
                        <TableCell>Ganz Kanada automatisch</TableCell>
                        <TableCell>Extra-Registrierung in anderen Provinzen</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Director-Residenz</TableCell>
                        <TableCell>25% müssen Canadian Residents sein</TableCell>
                        <TableCell>Variiert (BC: keine Anforderung)</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Gründungskosten</TableCell>
                        <TableCell>$200 CAD (online)</TableCell>
                        <TableCell>$275-400 CAD (je nach Provinz)</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Für wen ideal</TableCell>
                        <TableCell>Kanadaweites Geschäft, starker Markenname</TableCell>
                        <TableCell>Lokales Geschäft, keine Canadian Directors</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <div className="card-highlight p-6">
                  <p className="mb-0 text-sm">
                    <strong>Tipp für internationale Gründer ohne Canadian Residents im Board:</strong> 
                    Eine British Columbia Provincial Corporation hat keine Residenzanforderungen für 
                    Directors — ideal, wenn Sie keinen kanadischen Direktor einsetzen können.
                  </p>
                </div>
              </section>

              {/* Schritt für Schritt */}
              <section id="schritt-fuer-schritt" className="mb-16">
                <h2>Schritt-für-Schritt: Kanadische Corporation gründen</h2>

                <div className="space-y-6">
                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        1
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          NUANS-Namenssuche
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Führen Sie eine NUANS-Suche durch, um sicherzustellen, dass Ihr gewünschter 
                          Firmenname einzigartig ist. Der Report ist 90 Tage gültig und kostet ca. $20 CAD.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        2
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Articles of Incorporation
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Erstellen und einreichen der Gründungsdokumente. Enthält Firmenname, 
                          Registered Office, Aktienstruktur und Beschränkungen.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        3
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Directors benennen
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Ernennen Sie mindestens einen Director. Bei Federal Corporations müssen 25% 
                          Canadian Residents sein. Dokumentieren Sie die Ernennung schriftlich.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        4
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Registered Office einrichten
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Bestimmen Sie eine kanadische Geschäftsadresse. Virtual Office Services 
                          bieten diese Adresse für internationale Gründer an.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        5
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Business Number (BN) beantragen
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Registrieren Sie sich bei der Canada Revenue Agency (CRA) für eine 
                          Business Number. Diese benötigen Sie für Steuern und GST/HST.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        6
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Provinciale Registrierungen
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Je nach Geschäftstätigkeit sind zusätzliche Registrierungen auf 
                          Provinzebene erforderlich (Extra-Provincial Registration).
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        7
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Bankkonto eröffnen
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Eröffnen Sie ein kanadisches Geschäftskonto. Für Nicht-Residenten kann 
                          dies eine persönliche Reise nach Kanada erfordern.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Kosten */}
              <section id="kosten" className="mb-16">
                <h2>Kosten und Gebühren</h2>

                <div className="overflow-x-auto mb-8">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead>Jurisdiction</TableHead>
                        <TableHead>Gründungsgebühr</TableHead>
                        <TableHead>Bearbeitungszeit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {provincialFees.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{row.province}</TableCell>
                          <TableCell>{row.fee}</TableCell>
                          <TableCell>{row.processing}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <p>
                  <strong>Zusätzliche Kosten:</strong>
                </p>
                <ul>
                  <li>NUANS-Namenssuche: ~$20 CAD</li>
                  <li>Registered Office Service: $100-300 CAD/Jahr</li>
                  <li>Annual Return (Federal): $20 CAD</li>
                  <li>Buchhaltung/Steuererklärung: $500-2000+ CAD/Jahr</li>
                </ul>
              </section>

              {/* Steuern */}
              <section id="steuern" className="mb-16">
                <h2>Steuern in Kanada</h2>

                <h3 className="mt-8 mb-4">Körperschaftsteuer</h3>
                <p>
                  Die kombinierte Körperschaftsteuer (Federal + Provincial) liegt zwischen 23% und 31%, 
                  abhängig von der Provinz. Der Bundessteuersatz beträgt 15% (bzw. 9% für die ersten 
                  $500.000 CAD für qualifizierte kleine Unternehmen).
                </p>

                <h3 className="mt-8 mb-4">Small Business Deduction</h3>
                <p>
                  Canadian-Controlled Private Corporations (CCPC) mit aktivem Geschäftseinkommen 
                  profitieren von einem reduzierten Steuersatz auf die ersten $500.000 CAD pro Jahr. 
                  Der effektive kombinierte Satz liegt dann bei etwa 9-12%.
                </p>

                <h3 className="mt-8 mb-4">GST/HST (Umsatzsteuer)</h3>
                <p>
                  Die Goods and Services Tax (GST) beträgt 5% auf Bundesebene. In einigen Provinzen 
                  ist sie mit der Provincial Sales Tax zur Harmonized Sales Tax (HST) von 13-15% 
                  kombiniert.
                </p>

                <h3 className="mt-8 mb-4">Doppelbesteuerungsabkommen mit Deutschland</h3>
                <p>
                  Das DBA zwischen Deutschland und Kanada vermeidet die doppelte Besteuerung von 
                  Einkommen. Die genauen Auswirkungen hängen von Ihrer Situation ab — ein Steuerberater 
                  mit internationalem Schwerpunkt ist empfehlenswert.
                </p>
              </section>

              {/* FAQ */}
              <section id="faq" className="mb-16">
                <h2>Häufig gestellte Fragen</h2>
                
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`faq-${index}`}>
                      <AccordionTrigger className="text-left font-heading font-semibold text-navy-900">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>

            </article>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="space-y-8">
              <TableOfContents items={tocItems} />

              <CTABox
                title="US-LLC als Alternative?"
                provider="Northwest Registered Agent"
                rating={4.9}
                variant="sidebar"
                highlights={[
                  'Ab $39 + Staatsgebühren',
                  'Keine Director-Residenzpflicht',
                  'Mehr Datenschutz'
                ]}
              />

              <div className="bg-slate-50 border border-slate-200 rounded-sm p-6">
                <h4 className="font-heading font-bold text-navy-900 mb-4">Verwandte Ratgeber</h4>
                <ul className="space-y-3">
                  <li>
                    <Link to="/llc-usa-gruenden" className="text-sm text-slate-700 hover:text-teal-600">
                      → LLC Gründung USA
                    </Link>
                  </li>
                  <li>
                    <Link to="/ratgeber/corporation-ontario-gruenden" className="text-sm text-slate-700 hover:text-teal-600">
                      → Corporation in Ontario gründen
                    </Link>
                  </li>
                  <li>
                    <Link to="/ltd-uk-gruenden" className="text-sm text-slate-700 hover:text-teal-600">
                      → LTD Gründung UK
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CanadaPage;
